<template>
  <v-container class="submit-start">
    <!-- META-DATA -->
    <vue-headful
      :title="'Submit Your Design' + this.$store.state.app.meta.titleSuffix"
      :description="this.$store.state.app.meta.description" />

    <beta-notice />

    <div class="header">
      <h3 class="header__title">Submit a Design</h3>
      <span class="header__subtitle">Share your designs with the world - submit to the MemerHats catalog.</span>
    </div>

    <v-divider />

    <p>Do you have something that you want to share with your friends, family, coworkers/subordinates, or discord meme group? Do you like not paying for patches? If so, Consider submitting your design to the MemerHats catalog:</p>
    <p>Submitted designs will be featured in our <router-link :to="{ name: 'patch-submissions' }">community showcase</router-link> for a limited time, where anyone can buy them. If enough people buy your design it will be immortalized in our permanent catalog, and every time someone buys your design you will get store credit!</p>
    <p>
      Before submitting a design:
      <ol>
        <li>brush up on how the submission program works (see below)</li>
        <li>check out our tips for how to format your image file (also below)</li>
        <li>review our terms and conditions (way down below, at the bottom of the page)</li>
      </ol>
      Then, click the big, succulent, red button.
    </p>
    <p><i>(If you just want to make a custom patch, that's <router-link :to="{ name: 'custom-start' }">this way</router-link>)</i></p>

    <v-divider />

    <div class="submit-start__action-row">
      <div
        v-if="!isModernBrowser"
        class="submit-start__lockout">
        <v-icon class="submit-start__lockout-icon">error</v-icon>
        <span>A modern browser is required in order to use this feature.</span>
      </div>
      <div
        v-if="isModernBrowser && !isAuthenticated"
        class="submit-start__lockout">
        <v-icon class="submit-start__lockout-icon">lock</v-icon>
        <span>You must be logged in to submit a design.</span>
      </div>
      <div
        v-if="isModernBrowser && isAuthenticated"
        class="get-started__wrapper">
        <v-btn
          :to="{
            path: '/submit/create'
          }"
          large
          color="primary"
          class="get-started">
          <h4 class="get-started__text">
            get<br>started
          </h4>
        </v-btn>
      </div>
    </div>

    <v-divider />

    <section>
      <h4
        slot="header"
        class="expansion-panel__header">
        How It Works
      </h4>
      <div class="expansion-panel__content">
        <h5 class="expansion-panel__subheading">1. Submit Your Design</h5>
        <p>When your artwork is ready, just click that big, red button up there to upload your image file. Once your artwork is uploaded you will be able to set all the details of your patch - the title, description, what tags describe your design, and what you want to be known as when you're a big shot.</p>
        <h5 class="expansion-panel__subheading">2. Tell Your Friends</h5>
        <p>Once your patch is submitted you will be given a URL for your patch. For {{ salesPeriod }} days, anyone with this URL will be able to buy your patch as if it were any other patch in our catalog - but after {{ salesPeriod }} days, your patch will be gone; You will need to share your URL with anyone who wants your patch.</p>
        <h5 class="expansion-panel__subheading">3. Die a Legend</h5>
        <p>The top-selling submitted patches at any given time will be featured in the <router-link :to="{ name: 'patch-submissions' }">community showcase</router-link>. Patches that sell exceptionally well during their {{ salesPeriod }}-day run will be automatically nominated for entry into the permanent catalog, where they will live on forever. If your patch makes it into the permanent catalog you will receive a percentage of the profits as store credit in perpetuity - enough that you might not need to pay for patches ever again.</p>
      </div>
    </section>

    <v-divider />

    <section>
      <h4>Submission Guidelines</h4>
      <div>
        <p>
          Reaching the sales threshold does not guarantee that your submission will be accepted to the MemerHats catalog - MemerHats reserves the right to reject any submission for any reason. Submissions will generally be rejected if they contain any of the following:
        </p>
        <ol>
          <li>Corporate logos or other unlicensed intellectual property</li>
          <li>Gratuitous obscenity or other Not-Safe-For-Work content</li>
          <li>Any illegal content</li>
          <li>
            Any content that violates MemerHats' <router-link :to="{ name: 'terms-and-conditions' }">Terms and Conditions</router-link> or the <router-link :to="{ name: 'artist-agreement' }">Artist Agreement</router-link>.
          </li>
          <li>Content that is too similar to any existing catalog patches</li>
        </ol>
        <br>
      </div>
    </section>

    <v-divider />

    <section>
      <h4
        slot="header"
        class="expansion-panel__header">
        tips for formatting your image
      </h4>
      <div class="expansion-panel__content">
        <p>The optimal size for your document is 1200 pixels wide and 600 pixels tall.</p>
        <p>While any valid image file type will work, a <code>.png</code> will work best.</p>
        <p>It's important to note that the colors on the patch will not be identical to those you see on your screen - though they will be very very close; this is normal when printing a digital image because of how colors are created on your screen vs. how they are created with ink. To best approximate what your patch will look like, set your document color space to <code>EPSON sRGB</code> if available, or another color space for printed media.</p>
      </div>
    </section>

    <v-divider />

    <!-- ==== TODO: FAQ ==== -->
    <!--
    Why not give people money instead of store credit?
    1. Can we afford to pay people real money? I think not, at least for a while.
    2. How does paying people work with filing taxes? Would we have to make a W2 for each person who designed a patch? I don't know, and I haven't researched it yet.
    3. The community submissions is not a sales platform. I think paying people real money would lead to exploitation of the platform, and ultimately low-quality and inauthentic designs - like what happens with t-shirt platforms that pay real money. By paying people with store credit, we create a positive flow *within* the community. There will be ways for professionals to monetize their designs - such as affiliate platforms - but the community submissions is not for that.
    -->
    <!-- ==== END FAQ ==== -->
  </v-container>
</template>

<script>
import api from '../../utils/api';

import BetaNotice from '../../components/BetaNotice.vue';

export default {
  name: 'SubmitStart',
  components: {
    BetaNotice,
  },
  data() {
    return {
      salesPeriod: 0,
    };
  },
  computed: {
    isModernBrowser() {
      return 'FileReader' in window;
    },
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
  },
  created() {
    api
      .getData('/storefront/')
      .then((res) => {
        this.salesPeriod = res.data.submissionsConfig.salesPeriod;
      });
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.submit-start
  display: flex
  flex-flow: column nowrap

  &__action-row
    display: flex
    flex-flow: column nowrap
    align-items: center
    justify-content: center
    width: 100%
    padding:$base-padding*2
    @media screen and (max-width: 48rem)
      padding-bottom: $base-padding*8

  &__lockout
    display: flex
    flex-flow: column nowrap
    align-items: center
    justify-content: center
    padding: $base-padding*2
    text-align: center
    color: $white
    background-color: $error
    border-radius: $base-border-radius*2
    &-icon
      color: inherit
      font-size: 8rem

.header
  padding: $base-padding*2 0
  text-align: center
  &__subtitle
    color: $accent--dark

.get-started
  height: 12rem
  width: 12rem
  border-radius: 50%
  z-index: 1
  &__text
    line-height: normal
    text-align: center
    text-transform: uppercase
  &__wrapper
    position: relative
    display: flex
    padding: $base-padding*4
    &::before,
    &::after
      content: ''
      position: absolute
      top: 0
      left: 0
      height: 100%
      width: 100%
      background-color: $primary
      opacity: 0
      border-radius: 50%
    &::before
      animation: pulse 4s infinite
    &::after
      animation: pulse 4s 2s infinite


@keyframes pulse
 0%
   top: 25%
   left: 25%
   height: 50%
   width: 50%
   opacity: 1
 75%
   top: 5%
   left: 5%
   height: 90%
   width: 90%
   opacity: .24
 100%
   top: 0
   left: 0
   height: 100%
   width: 100%
   opacity: 0

</style>
